import { Commercial, CommercialType } from '@talon/web/lib/api';
import { Box, CardHeader, CheckBox, Text } from 'grommet';
import { ReactNode } from 'react';
import { USD } from 'src/utils/currency';
import { CostTier, FieldTier } from '../CardTiers';
import { EditableText } from '../EditableText';
import { LabeledField } from '../LabeledField';
import { SelectableText } from '../SelectableText';

export type CommercialCardHeaderProps = {
  commercial?: Commercial;
  checked: boolean;
  onChecked: () => void;
  onTitle: (title: string) => void;
  onType: (type: CommercialType) => void;
  onDuration: (seconds: number) => void;
  /** Extra fields after the commercial's own, e.g. its contract */
  children?: ReactNode;
  /** The line under the fields, e.g. "3 usages" */
  summary: string;
  wages: number;
  total: number;
};

/**
 * A commercial's card header on the Usage Estimator and Edits pages, in the session card's two tiers:
 * the commercial's fields, then what it costs
 */
export const CommercialCardHeader = ({
  commercial,
  checked,
  onChecked,
  onTitle,
  onType,
  onDuration,
  children,
  summary,
  wages,
  total,
}: CommercialCardHeaderProps) => (
  <CardHeader direction="column" gap="none" align="stretch" pad="none">
    {/* Tier 1: the commercial's fields */}
    <FieldTier>
      <Box pad={{ bottom: '8px' }}>
        <CheckBox onChange={onChecked} checked={checked} a11yTitle={`Select ${commercial?.title ?? 'commercial'}`} />
      </Box>
      <LabeledField label="Commercial">
        <EditableText val={commercial?.title ?? ''} onChange={onTitle} />
      </LabeledField>
      <LabeledField label="Type">
        <SelectableText
          options={Object.values(CommercialType).map((t) => ({ label: t, value: t }))}
          val={commercial?.type}
          onChange={(val) => onType(val as CommercialType)}
        />
      </LabeledField>
      <LabeledField label="Length">
        <Box direction="row" gap="xxsmall" align="center">
          <EditableText
            val={commercial?.duration?.toString() ?? ''}
            onChange={(val) => onDuration(val ? parseInt(val) : 0)}
          />
          <Text>seconds</Text>
        </Box>
      </LabeledField>
      {children}
    </FieldTier>

    {/* Tier 2: what it costs */}
    <CostTier
      summary={summary}
      costs={[
        { label: 'Wages', value: USD(wages) },
        { label: 'Total', value: USD(total), emphasis: true },
      ]}
    />
  </CardHeader>
);
